<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import UserMultiSelect from "@/components/UserMultiSelect.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useTemplates } from "./useTemplates";
import { uploadImage, uploadVideo, uploadFile } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchUsers } from "@/features/users/api";
import { fetchMessage, addMessage, editMessage, sendGuidNotification, deleteTemplate, fetchOutletBranches } from "./api";

const DEFAULT_IMG = "https://firebasestorage.googleapis.com/v0/b/aspial.appspot.com/o/announcements%2Faspial.jpg?alt=media&token=ea877aa0-2ec4-4caa-9342-3280ceafb2fc";
const BTN_LINK_ITEMS = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Campaign", "Videos", "More Options"];

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);

const loading = ref(false), usersLoading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const users = ref([]), sendNotif = ref(true), previewOpen = ref(false);
let original = null;

const form = ref({ selectedUsers: [], notif_title: "", notif_content: "", title: "", content: "", startDate: "", endDate: "", btnTitle: "", btnLink: "", redirectUrl: "", imgUrl: DEFAULT_IMG, isExternalBrowser: false, isDynamicUrl: false });

const imageUploader = (file, p) => uploadImage("messages", file, p);
const redirectUploader = (file, p) => {
  if (file.type.includes("image")) return uploadImage("messages", file, p);
  if (file.type.includes("video")) return uploadVideo(file, p, "messages");
  return uploadFile("messages", file, p);
};

function mapUser(u) {
  return { id: u.id ?? u._id ?? u.objectID ?? u.guid, name: u.name, display_name: u.display_name, customer_id: u.customer_id, company: u.company, department: u.department, image: u.image };
}

// Appends the recipient's document id as `?id=`, replacing any id query from a previous save.
function withDynamicUserId(url, userId) {
  const base = url.split("?")[0];
  return `${base}?id=${userId}`;
}

onMounted(async () => {
  loading.value = true; usersLoading.value = true;
  fetchUsers().then(list => (users.value = (list || []).map(mapUser))).catch(() => (error.value = "Couldn't load the user list.")).finally(() => (usersLoading.value = false));
  try {
    if (isEdit.value) {
      const m = await fetchMessage(id.value);
      if (!m) { notFound.value = true; return; }
      original = m;
      form.value = { selectedUsers: Array.isArray(m.userGuids) ? [...m.userGuids] : [], notif_title: m.notif_title ?? "", notif_content: m.notif_content ?? "", title: m.title ?? "", content: m.content ?? "", startDate: toDateTimeLocal(m.startDate), endDate: toDateTimeLocal(m.endDate), btnTitle: m.btnTitle ?? "", btnLink: m.btnLink ?? "", redirectUrl: m.redirectUrl ?? "", imgUrl: m.imgUrl || DEFAULT_IMG, isExternalBrowser: !!m.isExternalBrowser, isDynamicUrl: !!m.isDynamicUrl };
    }
  } catch { error.value = "Couldn't load the message."; }
  finally { loading.value = false; }
});

// Outlet staff
const selectingOutlet = ref(false);
async function selectAllOutletStaff() {
  selectingOutlet.value = true; error.value = "";
  try {
    const branches = await fetchOutletBranches();
    const branchNames = new Set(branches.map(b => b.branch));
    form.value.selectedUsers = users.value.filter(u => branchNames.has(u.department)).map(u => u.id);
  } catch { error.value = "Couldn't load outlet branches (check VITE_HOST_DOMAIN)."; }
  finally { selectingOutlet.value = false; }
}

// Templates
const { templates, templatesOpen, templatesLoading, openTemplates, closeTemplates } = useTemplates(msg => { error.value = msg; });
const templateToDelete = ref(null), deletingTemplate = ref(false);
function applyTemplate(t) {
  form.value = { ...form.value, imgUrl: t.imgUrl || DEFAULT_IMG, title: t.title ?? "", notif_title: t.notif_title ?? "", notif_content: t.notif_content ?? "", content: t.content ?? "", btnTitle: t.btnTitle ?? "", btnLink: t.btnLink ?? "", redirectUrl: t.redirectUrl ?? "", isExternalBrowser: !!t.isExternalBrowser, isDynamicUrl: !!t.isDynamicUrl };
  closeTemplates();
}
async function confirmDeleteTemplate() {
  if (!templateToDelete.value?._id) return;
  deletingTemplate.value = true;
  try { await deleteTemplate(templateToDelete.value._id); templates.value = templates.value.filter(t => t._id !== templateToDelete.value._id); }
  catch { error.value = "Couldn't delete the template."; }
  finally { deletingTemplate.value = false; templateToDelete.value = null; closeTemplates(); }
}

function validate() {
  const f = form.value;
  if (!f.selectedUsers.length) return "Select at least one recipient.";
  if (!f.notif_title.trim()) return "Enter a notification title.";
  if (!f.title.trim()) return "Enter an inbox title.";
  if (!f.startDate || !f.endDate) return "Start and end dates are required.";
  if (isEdit.value && f.isDynamicUrl && f.selectedUsers.length > 1) return "Dynamic User ID notices support only one recipient when editing.";
  return "";
}

function basePayload() {
  const f = form.value;
  return { ...(original || {}), imgUrl: f.imgUrl, notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, content: f.content, startDate: new Date(f.startDate), endDate: new Date(f.endDate), btnTitle: f.btnTitle, btnLink: f.btnLink, redirectUrl: f.redirectUrl, userGuids: f.selectedUsers, msgContent: Object.assign({}, f.selectedUsers.map(u => [u])), isExternalBrowser: f.isExternalBrowser, isDynamicUrl: f.isDynamicUrl };
}

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const msg = validate();
  if (msg) { validationError.value = msg; return; }
  const f = form.value;
  saving.value = true;
  try {
    let lastResult;
    if (isEdit.value) {
      if (f.isDynamicUrl && f.redirectUrl) {
        const userId = f.selectedUsers[0];
        lastResult = await editMessage(id.value, { ...basePayload(), redirectUrl: withDynamicUserId(f.redirectUrl, userId), userGuids: [userId] });
      } else {
        lastResult = await editMessage(id.value, basePayload());
      }
    } else if (f.isDynamicUrl && f.redirectUrl) {
      for (const userId of f.selectedUsers) {
        lastResult = await addMessage({ ...basePayload(), redirectUrl: withDynamicUserId(f.redirectUrl, userId), userGuids: [userId] });
      }
    } else {
      lastResult = await addMessage(basePayload());
    }
    if (sendNotif.value) {
      await sendGuidNotification({ notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, bodyMessage: f.content, userGuids: f.selectedUsers, startDate: f.startDate, endDate: f.endDate, objectId: lastResult?._id ?? lastResult }).catch(() => {});
    }
    success.value = true; setTimeout(() => router.push({ name: "messages" }), 800);
  } catch { error.value = "Couldn't save the message. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'messages' })">Back</VBtn>
    <div class="d-flex align-center justify-space-between mb-4">
      <h1 class="text-h5">{{ isEdit ? "Edit Notice" : "Create Notice" }}</h1>
      <VBtn variant="tonal" @click="openTemplates">Use Template</VBtn>
    </div>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Message not found.</VAlert>
    <VCard v-else class="pa-4" max-width="820">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <UploadField v-model="form.imgUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12">
            <UserMultiSelect v-model="form.selectedUsers" :users="users" :loading="usersLoading" label="Recipients" />
          </VCol>
          <VCol cols="12">
            <VBtn variant="tonal" size="small" :loading="selectingOutlet" @click="selectAllOutletStaff">Select All MC (Outlet Staff)</VBtn>
          </VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12"><VTextField v-model="form.notif_title" label="Notification Title" /></VCol>
          <VCol cols="12"><VTextField v-model="form.notif_content" label="Notification Content" /></VCol>
          <VCol cols="12"><VDivider /></VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Inbox Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Inbox Content" :image-uploader="imageUploader" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="6">
            <VAutocomplete v-model="form.btnLink" label="Button Link" :items="BTN_LINK_ITEMS" clearable placeholder="—" auto-select-first />
          </VCol>
          <VCol v-if="!form.btnLink" cols="12"><VTextField v-model="form.redirectUrl" label="Button URL" /></VCol>
          <VCol v-if="!form.btnLink" cols="12">
            <UploadField v-model="form.redirectUrl" kind="file" label="Upload PDF / Image for button" accept="image/*,application/pdf,video/*" :uploader="redirectUploader" />
          </VCol>
          <VCol cols="6"><VSwitch v-model="form.isExternalBrowser" label="External Browser" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isDynamicUrl" label="Dynamic User ID" hide-details /></VCol>
          <VCol cols="12"><VCheckbox v-model="sendNotif" label="Broadcast notice (send push notification)" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Submit" }}</VBtn>
          <VBtn variant="tonal" @click="previewOpen = true">Preview</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'messages' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>

    <!-- Templates dialog -->
    <VDialog v-model="templatesOpen" max-width="540">
      <VCard title="Use a Template">
        <VCardText>
          <p v-if="templatesLoading" class="text-medium-emphasis">Loading templates…</p>
          <p v-else-if="!templates.length" class="text-medium-emphasis">No templates saved.</p>
          <VList v-else>
            <VListItem v-for="t in templates" :key="t._id" :title="t.title || 'Untitled'">
              <template #append>
                <VBtn size="small" class="mr-2" @click="applyTemplate(t)">Use</VBtn>
                <VBtn size="small" color="error" variant="tonal" @click="templateToDelete = t; closeTemplates()">Delete</VBtn>
              </template>
            </VListItem>
          </VList>
        </VCardText>
        <VCardActions class="justify-end px-4 pb-4">
          <VBtn variant="tonal" @click="closeTemplates">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ConfirmDialog :model-value="templateToDelete !== null" title="Delete template?" message="Permanently delete this template?" :loading="deletingTemplate" @update:model-value="templateToDelete = null" @confirm="confirmDeleteTemplate" />

    <!-- Preview dialog -->
    <VDialog v-model="previewOpen" max-width="400">
      <VCard title="Preview">
        <VCardText>
          <img v-if="form.imgUrl" :src="form.imgUrl" alt="" style="width:100%;max-height:160px;object-fit:contain;border-radius:6px;margin-bottom:8px" />
          <div class="text-h6 mb-2">{{ form.title || "Inbox Title" }}</div>
          <div class="text-body-2 text-medium-emphasis" v-html="form.content || '<p>Inbox content…</p>'" />
          <VBtn v-if="form.btnTitle" size="small" class="mt-3">{{ form.btnTitle }}</VBtn>
        </VCardText>
        <VCardActions class="justify-end px-4 pb-4"><VBtn variant="tonal" @click="previewOpen = false">Close</VBtn></VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
